import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { SimpleDataCacheService } from '@/lib/utils/simple-data-cache-service';
import { sortPeople, getPersonByName as getPersonByNameWithFuzzyMatch } from './people-data-service';

interface PeopleDataContextType {
  people: Person[];
  getPersonById: (id: string) => Person | undefined;
  getPersonByName: (personName: string) => Person | undefined;
}

export const PeopleDataContext = createContext<PeopleDataContextType | undefined>(undefined);

export function usePeopleData() {
  const context = useContext(PeopleDataContext);
  if (!context) {
    throw new Error('usePeopleData must be used within PeopleDataProvider');
  }
  return context;
}

interface PeopleDataProviderProps {
  children: ReactNode;
  people: Person[];
}

export function PeopleDataProvider({ children, people: initialData }: PeopleDataProviderProps) {
  const [cacheService] = useState(() =>
    new SimpleDataCacheService<Person>(
      initialData,
      sortPeople
    )
  );

  // Track the current items in state so the component re-renders when they change
  const [people, setPeople] = useState<Person[]>(initialData);

  useEffect(() => {
    cacheService.setNewItems(initialData);
    setPeople(cacheService.getItems());
  }, [initialData, cacheService]);

  // Build lookup maps for O(1) access
  const lookupMaps = useState(() => {
    const byId = new Map<string, Person>();
    const byName = new Map<string, Person>();
    const byAlternateName = new Map<string, Person>();

    return { byId, byName, byAlternateName };
  })[0];

  // Rebuild lookup maps when people change
  useEffect(() => {
    lookupMaps.byId.clear();
    lookupMaps.byName.clear();
    lookupMaps.byAlternateName.clear();

    people.forEach(person => {
      if (person.id) {
        lookupMaps.byId.set(person.id, person);
      }
      if (person.personName) {
        lookupMaps.byName.set(person.personName.toLowerCase(), person);
      }

      person.alternateNames?.forEach(altName => {
        if (altName) {
          lookupMaps.byAlternateName.set(altName.toLowerCase(), person);
        }
      });
    });
  }, [people, lookupMaps]);

  const getPersonById = (id: string): Person | undefined => {
    if (!id) return undefined;
    return lookupMaps.byId.get(id);
  };

  const getPersonByName = (personName: string): Person | undefined => {
    if (!personName) return undefined;

    const lowerName = personName.toLowerCase();

    // 1. Try exact match (O(1))
    let match = lookupMaps.byName.get(lowerName);
    if (match) return match;

    // 2. Try alternate names (O(1))
    match = lookupMaps.byAlternateName.get(lowerName);
    if (match) return match;

    // 3. Fall back to fuzzy matching for edge cases (O(n))
    return getPersonByNameWithFuzzyMatch(cacheService.getItems(), personName);
  };

  return (
    <PeopleDataContext.Provider
      value={{
        people: cacheService.getItems(),
        getPersonById,
        getPersonByName
      }}
    >
      {children}
    </PeopleDataContext.Provider>
  );
}

